/**
 * TenantSelect.tsx — wybór tenanta dla multi-tenant usera (F7b T7b.3).
 *
 * Host: `app.<base>` (public scope). Path: `/select?email=X`.
 *
 * Flow:
 *  1. Odczytaj `email` z query param. Brak → redirect `/login`.
 *  2. Refetch `POST /auth/tenant-lookup` z tym emailem (źródło prawdy — query param
 *     może być manualnie zmieniony, backend i tak waliduje).
 *  3. Render listy tenantów jako karty. Klik → redirect na `<slug>.app.<base>/login?email=X`.
 *
 * Edge cases:
 *  - 0 tenants (np. user ręcznie wpisał email w URL) → info alert + przycisk powrotu.
 *  - 1 tenant → auto-redirect (skip, consistency z PublicLogin 1-tenant branch).
 *  - 429 / błąd lookup → alert + retry.
 *
 * D3 uzasadnienie: osobny ekran (nie dropdown w topbarze) — brak cross-subdomain
 * state sharing, prostszy state management per subdomena. Dropdown szybkiego
 * przełączenia → F10.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';
import { buildTenantLoginUrl } from '@/lib/tenant-url';

interface TenantEntry {
  tenantId: string;
  slug: string;
  name: string;
  displayName?: string;
}

interface TenantLookupResponse {
  tenants: TenantEntry[];
}

type State =
  | { kind: 'loading' }
  | { kind: 'ok'; tenants: TenantEntry[] }
  | { kind: 'error'; code: 'rate_limited' | 'generic' };

export function TenantSelect() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const email = useMemo(() => params.get('email') ?? '', [params]);
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!email) return; // Navigate poniżej obsłuży redirect.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post<TenantLookupResponse>('/auth/tenant-lookup', { email });
        if (cancelled) return;
        const list = data.tenants ?? [];
        if (list.length === 1) {
          // Single-tenant → skip select, consistency z PublicLogin.
          window.location.href = buildTenantLoginUrl({ slug: list[0]!.slug, email });
          return;
        }
        setState({ kind: 'ok', tenants: list });
      } catch (err) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } }).response?.status;
        setState({
          kind: 'error',
          code: status === 429 ? 'rate_limited' : 'generic',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [email]);

  if (!email) {
    return <Navigate to="/login" replace />;
  }

  const onSelect = (slug: string) => {
    window.location.href = buildTenantLoginUrl({ slug, email });
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-text-primary">{t('tenant.select.title')}</h1>
          <p className="mt-2 text-sm text-text-tertiary">
            {t('tenant.select.subtitle', { email })}
          </p>
        </div>

        {state.kind === 'loading' && (
          <div className="flex items-center justify-center py-10" role="status" aria-live="polite">
            <Spinner />
          </div>
        )}

        {state.kind === 'error' && (
          <div className="space-y-4">
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {state.code === 'rate_limited'
                ? t('tenant.select.errors.rateLimited')
                : t('tenant.select.errors.generic')}
            </div>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => window.location.replace('/login')}
            >
              {t('tenant.select.backToLogin')}
            </Button>
          </div>
        )}

        {state.kind === 'ok' && state.tenants.length === 0 && (
          <div className="space-y-4">
            <div
              role="status"
              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {t('tenant.select.info.noTenants')}
            </div>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => window.location.replace('/login')}
            >
              {t('tenant.select.backToLogin')}
            </Button>
          </div>
        )}

        {state.kind === 'ok' && state.tenants.length > 0 && (
          <ul className="space-y-3">
            {state.tenants.map((tenant) => (
              <li key={tenant.tenantId}>
                <Card
                  padding="none"
                  className="cursor-pointer transition hover:border-brand-500 hover:shadow-md"
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(tenant.slug)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(tenant.slug);
                    }
                  }}
                  aria-label={t('tenant.select.chooseAriaLabel', { name: tenant.name })}
                >
                  <div className="flex items-center justify-between p-4">
                    <div>
                      <div className="font-semibold text-text-primary">{tenant.name}</div>
                      <div className="text-xs text-text-tertiary">
                        {tenant.slug}
                        {tenant.displayName ? ` · ${tenant.displayName}` : ''}
                      </div>
                    </div>
                    <span aria-hidden className="text-text-tertiary">
                      →
                    </span>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
